import { createFileRoute } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { listPayoutLedger, type LedgerRow } from "@/lib/payout-ledger.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/admin/payout-ledger")({
  head: () => ({ meta: [{ title: "Payout ledger — Admin" }] }),
  component: PayoutLedgerPage,
  errorComponent: ({ error }) => <div className="p-8 text-sm text-destructive">{error.message}</div>,
  notFoundComponent: () => <div className="p-8">Not found.</div>,
});

const fmt = (p: number | null) => p === null ? "—" : `£${(p / 100).toFixed(2)}`;

const STATUS_TONE: Record<LedgerRow["status"], string> = {
  ok: "text-emerald-700",
  variance: "text-destructive",
  missing_account: "text-amber-700",
  no_stripe_sdk: "text-muted-foreground",
  stripe_error: "text-destructive",
};

const NAME_RE = /^[\p{L}\p{N}\s'.\-&]*$/u;
const filterSchema = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "From date is invalid."),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "To date is invalid."),
  venueQuery: z.string().trim().max(120, "Venue search is too long (max 120).").regex(NAME_RE, "Venue search contains invalid characters."),
  bartenderQuery: z.string().trim().max(120, "Bartender search is too long (max 120).").regex(NAME_RE, "Bartender search contains invalid characters."),
}).superRefine((v, ctx) => {
  const f = new Date(v.from).getTime();
  const t = new Date(v.to).getTime();
  if (Number.isNaN(f) || Number.isNaN(t)) { ctx.addIssue({ code: "custom", path: ["from"], message: "Dates are invalid." }); return; }
  if (f > t) ctx.addIssue({ code: "custom", path: ["to"], message: "To date must be on or after From date." });
  const days = (t - f) / 86_400_000;
  if (days > 366) ctx.addIssue({ code: "custom", path: ["to"], message: "Date range cannot exceed 366 days." });
  const todayMs = Date.now() + 86_400_000;
  if (f > todayMs) ctx.addIssue({ code: "custom", path: ["from"], message: "From date cannot be in the future." });
});

type SortKey = "variance" | "app_paid" | "stripe_paid" | "shift_costs" | "name" | "status";
const SORT_LABEL: Record<SortKey, string> = {
  variance: "Variance",
  app_paid: "App paid",
  stripe_paid: "Stripe paid",
  shift_costs: "Shift costs",
  name: "Bartender name",
  status: "Status",
};
const PAGE_SIZES = [10, 25, 50, 100] as const;

function sortRows(rows: LedgerRow[], key: SortKey, dir: "asc" | "desc"): LedgerRow[] {
  const mult = dir === "asc" ? 1 : -1;
  const get = (r: LedgerRow): number | string => {
    switch (key) {
      case "variance": return Math.abs(r.variance_pence ?? -1);
      case "app_paid": return r.app_paid_pence;
      case "stripe_paid": return r.stripe_paid_pence ?? -1;
      case "shift_costs": return r.shift_costs_pence;
      case "name": return (r.full_name ?? "").toLowerCase();
      case "status": return r.status;
    }
  };
  return [...rows].sort((a, b) => {
    const av = get(a); const bv = get(b);
    if (typeof av === "number" && typeof bv === "number") return (av - bv) * mult;
    return String(av).localeCompare(String(bv)) * mult;
  });
}

function PayoutLedgerPage() {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [venueQuery, setVenueQuery] = useState("");
  const [bartenderQuery, setBartenderQuery] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [appliedFilters, setAppliedFilters] = useState<{ from: string; to: string; venueQuery: string; bartenderQuery: string } | null>(null);
  const [rows, setRows] = useState<LedgerRow[]>([]);
  const [totals, setTotals] = useState<{ app_paid_pence: number; stripe_paid_pence: number; variance_pence: number } | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("variance");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [pageSize, setPageSize] = useState<number>(25);
  const [page, setPage] = useState<number>(1);

  const sorted = useMemo(() => sortRows(rows, sortKey, sortDir), [rows, sortKey, sortDir]);
  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const pageRows = useMemo(() => sorted.slice((safePage - 1) * pageSize, safePage * pageSize), [sorted, safePage, pageSize]);

  const mut = useMutation({
    mutationFn: listPayoutLedger,
    onSuccess: (res) => {
      if ("error" in res) { toast.error(res.error); return; }
      setRows(res.rows); setTotals(res.totals); setPage(1);
      toast.success(`${res.rows.length} bartender${res.rows.length === 1 ? "" : "s"} reconciled.`);
    },
  });

  const run = () => {
    const candidate = { from, to, venueQuery: venueQuery.trim(), bartenderQuery: bartenderQuery.trim() };
    const parsed = filterSchema.safeParse(candidate);
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const field = String(issue.path[0] ?? "form");
        if (!errs[field]) errs[field] = issue.message;
      }
      setErrors(errs);
      const first = parsed.error.issues[0];
      if (first) toast.error(first.message);
      return;
    }
    setErrors({});
    setAppliedFilters(parsed.data);
    mut.mutate({ data: parsed.data });
  };

  const exportCsv = () => {
    if (!sorted.length || !appliedFilters) return;
    const meta = [
      `# Payout ledger export`,
      `# from,${appliedFilters.from}`,
      `# to,${appliedFilters.to}`,
      `# venue_query,${appliedFilters.venueQuery || "(all)"}`,
      `# bartender_query,${appliedFilters.bartenderQuery || "(all)"}`,
      `# sort,${sortKey} ${sortDir}`,
      `# rows,${sorted.length}`,
    ].join("\n");
    const header = ["bartender_id","full_name","stripe_account_id","payouts_enabled","app_paid_pence","app_payable_pence","app_failed_pence","payout_count","stripe_paid_pence","stripe_payout_count","variance_pence","shift_costs_pence","status","note"];
    const esc = (v: unknown) => v === null || v === undefined ? "" : `"${String(v).replace(/"/g, '""')}"`;
    const csv = [meta, header.join(","), ...sorted.map((r) => header.map((h) => esc((r as unknown as Record<string, unknown>)[h])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const slug = [appliedFilters.from, "to", appliedFilters.to, appliedFilters.venueQuery && `venue-${appliedFilters.venueQuery}`, appliedFilters.bartenderQuery && `bartender-${appliedFilters.bartenderQuery}`].filter(Boolean).join("-").replace(/[^a-z0-9-]/gi, "_");
    const a = document.createElement("a"); a.href = url; a.download = `payout-ledger-${slug}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const headerCell = (key: SortKey, label: string, align: "left" | "right" = "left") => (
    <th className={`p-3 ${align === "right" ? "text-right" : ""}`}>
      <button
        type="button"
        onClick={() => {
          if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
          else { setSortKey(key); setSortDir(key === "name" || key === "status" ? "asc" : "desc"); }
          setPage(1);
        }}
        className="inline-flex items-center gap-1 hover:text-foreground"
      >
        {label}{sortKey === key && <span aria-hidden>{sortDir === "asc" ? "▲" : "▼"}</span>}
      </button>
    </th>
  );

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <p className="text-[11px] tracking-wide-luxury text-muted-foreground">Admin · Finance</p>
        <h1 className="text-3xl font-light">Payout ledger</h1>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
          Reconciles app-side shift costs and payout records against Stripe payouts for
          each bartender's Connect account. Variances over £0.01 are flagged for review.
        </p>

        <div className="mt-6 grid gap-3 border border-border p-6 sm:grid-cols-2 lg:grid-cols-4 lg:items-start">
          <Field label="From" error={errors.from}>
            <Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} aria-invalid={!!errors.from} />
          </Field>
          <Field label="To" error={errors.to}>
            <Input type="date" value={to} min={from} max={today} onChange={(e) => setTo(e.target.value)} aria-invalid={!!errors.to} />
          </Field>
          <Field label="Venue name" error={errors.venueQuery} hint={`${venueQuery.length}/120`}>
            <Input placeholder="Any venue" maxLength={120} value={venueQuery} onChange={(e) => setVenueQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} aria-invalid={!!errors.venueQuery} />
          </Field>
          <Field label="Bartender name" error={errors.bartenderQuery} hint={`${bartenderQuery.length}/120`}>
            <Input placeholder="Any bartender" maxLength={120} value={bartenderQuery} onChange={(e) => setBartenderQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} aria-invalid={!!errors.bartenderQuery} />
          </Field>
          <div className="flex flex-wrap gap-2 sm:col-span-2 lg:col-span-4">
            <Button disabled={mut.isPending} onClick={run}>{mut.isPending ? "Reconciling…" : "Run reconciliation"}</Button>
            <Button variant="outline" disabled={!sorted.length} onClick={exportCsv}>
              Export CSV{sorted.length ? ` (${sorted.length})` : ""}
            </Button>
            {(venueQuery || bartenderQuery) && (
              <Button variant="ghost" onClick={() => { setVenueQuery(""); setBartenderQuery(""); setErrors((e) => ({ ...e, venueQuery: "", bartenderQuery: "" })); }}>Clear filters</Button>
            )}
          </div>
        </div>

        {totals && (
          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            <Stat label="App paid total" value={fmt(totals.app_paid_pence)} />
            <Stat label="Stripe paid total" value={fmt(totals.stripe_paid_pence)} />
            <Stat label="Net variance" value={fmt(totals.variance_pence)} tone={Math.abs(totals.variance_pence) > 1 ? "text-destructive" : "text-emerald-700"} />
          </div>
        )}

        {sorted.length > 0 && (
          <>
            <div className="mt-6 flex flex-wrap items-end justify-between gap-3">
              <div className="flex flex-wrap items-end gap-3">
                <div className="w-44">
                  <Label className="text-xs">Sort by</Label>
                  <Select value={sortKey} onValueChange={(v) => { setSortKey(v as SortKey); setPage(1); }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(Object.keys(SORT_LABEL) as SortKey[]).map((k) => (
                        <SelectItem key={k} value={k}>{SORT_LABEL[k]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-32">
                  <Label className="text-xs">Direction</Label>
                  <Select value={sortDir} onValueChange={(v) => { setSortDir(v as "asc" | "desc"); setPage(1); }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="desc">Descending</SelectItem>
                      <SelectItem value="asc">Ascending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-32">
                  <Label className="text-xs">Page size</Label>
                  <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZES.map((n) => (<SelectItem key={n} value={String(n)}>{n} / page</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Showing {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, sorted.length)} of {sorted.length}
              </p>
            </div>

            <div className="mt-3 overflow-x-auto border border-border">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/30 text-left text-[11px] uppercase tracking-wide-luxury text-muted-foreground">
                  <tr>
                    {headerCell("name", "Bartender")}
                    <th className="p-3">Connect account</th>
                    {headerCell("shift_costs", "Shift costs", "right")}
                    {headerCell("app_paid", "App paid", "right")}
                    <th className="p-3 text-right">App pending</th>
                    {headerCell("stripe_paid", "Stripe paid", "right")}
                    {headerCell("variance", "Variance", "right")}
                    {headerCell("status", "Status")}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r) => (
                    <tr key={r.bartender_id} className="border-b border-border/60">
                      <td className="p-3">
                        <p className="font-medium">{r.full_name ?? "—"}</p>
                        <p className="text-xs text-muted-foreground font-mono">{r.bartender_id.slice(0, 8)}…</p>
                      </td>
                      <td className="p-3 font-mono text-xs">{r.stripe_account_id ?? <span className="text-amber-700">none</span>}</td>
                      <td className="p-3 text-right">{fmt(r.shift_costs_pence)}</td>
                      <td className="p-3 text-right">{fmt(r.app_paid_pence)}<span className="ml-1 text-xs text-muted-foreground">({r.payout_count})</span></td>
                      <td className="p-3 text-right">{fmt(r.app_payable_pence)}</td>
                      <td className="p-3 text-right">{fmt(r.stripe_paid_pence)}{r.stripe_payout_count !== null && <span className="ml-1 text-xs text-muted-foreground">({r.stripe_payout_count})</span>}</td>
                      <td className="p-3 text-right">{fmt(r.variance_pence)}</td>
                      <td className={`p-3 text-xs ${STATUS_TONE[r.status]}`}>
                        {r.status.replace(/_/g, " ")}
                        {r.note && <p className="text-[11px] text-muted-foreground">{r.note}</p>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex items-center justify-between gap-3">
              <Button variant="outline" size="sm" disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</Button>
              <p className="text-xs text-muted-foreground">Page {safePage} of {pageCount}</p>
              <Button variant="outline" size="sm" disabled={safePage >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>Next</Button>
            </div>
          </>
        )}

        {!mut.isPending && totals && rows.length === 0 && (
          <p className="mt-6 text-sm text-muted-foreground">No payouts or completed shifts in this window.</p>
        )}
      </main>
    </div>
  );
}

function Field({ label, error, hint, children }: { label: string; error?: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <Label>{label}</Label>
      {children}
      <div className="mt-1 flex items-center justify-between gap-2">
        <p className="text-[11px] text-destructive">{error ?? ""}</p>
        {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
      </div>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="border border-border p-4">
      <p className="text-[11px] uppercase tracking-wide-luxury text-muted-foreground">{label}</p>
      <p className={`mt-2 font-display text-2xl ${tone ?? ""}`}>{value}</p>
    </div>
  );
}
